import type { ReactElement } from "react"
import { useState } from "react"
import BaseContainer from "@gatsby-plugin-generic-page/components/BaseContainer"
import BaseSection from "@gatsby-plugin-generic-page/components/BaseSection"
import Header from "@gatsby-plugin-generic-page/components/Header"
import MarkdownText from "@gatsby-plugin-generic-page/components/Markdown/Text"
import ResponsiveImage from "@gatsby-plugin-generic-page/components/ResponsiveImage"
import type {
  StepAccordionSectionFlatData,
  StepItem,
} from "@gatsby-plugin-generic-page/fragments/stepAccordionSection"
import { loadIcon as loadSystemIcon } from "@ionos-web-design-system/icon/system/loader"
import { Stepper, useDynamicIcon } from "@ionos-web-design-system/react"
import isEmpty from "~/lib/isEmpty"

type StepAccordionItemProps = { step: StepItem }

function StepAccordionItem({
  step,
}: StepAccordionItemProps): ReactElement<StepAccordionItemProps> {
  const { icon: dynamicIcon } = useDynamicIcon(
    loadSystemIcon,
    step.icon?.iconId ?? ""
  )
  const { icon: dynamicActiveIcon } = useDynamicIcon(
    loadSystemIcon,
    step.activeIcon?.iconId ?? ""
  )

  const stepIcon = !isEmpty(step.icon) && dynamicIcon ? dynamicIcon : undefined
  const stepActiveIcon =
    stepIcon && !isEmpty(step.activeIcon) && dynamicActiveIcon
      ? dynamicActiveIcon
      : undefined

  return (
    <Stepper.Step icon={stepIcon} activeIcon={stepActiveIcon}>
      <Stepper.Step.Label>{step.title}</Stepper.Step.Label>

      {!isEmpty(step.text) ? (
        <Stepper.Step.Panel>
          <MarkdownText>{step.text}</MarkdownText>
        </Stepper.Step.Panel>
      ) : null}
    </Stepper.Step>
  )
}

function StepAccordionSection({
  header,
  isHeaderInGrid,
  accordionSteps,
  sectionSettings,
}: StepAccordionSectionFlatData): ReactElement<StepAccordionSectionFlatData> {
  const [activeStep, setActiveStep] = useState<number>(0)

  const activeImages = accordionSteps[activeStep]?.responsiveImages

  const headerElement = !isEmpty(header) ? <Header {...header} /> : null

  return (
    <BaseSection {...sectionSettings}>
      <BaseContainer {...sectionSettings?.containerSettings}>
        {!isHeaderInGrid && !isEmpty(header) ? (
          <div className="mb-7">{headerElement}</div>
        ) : null}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          <div className="order-2 lg:order-1">
            {isHeaderInGrid ? (
              <div className="mb-3">{headerElement}</div>
            ) : null}

            <Stepper
              variant="accordion"
              activeStep={activeStep}
              onChange={setActiveStep}
            >
              {accordionSteps.map((step, index) => (
                <StepAccordionItem key={`${step.title}-${index}`} step={step} />
              ))}
            </Stepper>
          </div>

          {!isEmpty(activeImages) ? (
            <div
              key={activeStep}
              className="order-1 lg:order-2 animate-[uds-content-enter_0.5s_ease-out_forwards]"
            >
              <ResponsiveImage images={activeImages} />
            </div>
          ) : null}
        </div>
      </BaseContainer>
    </BaseSection>
  )
}

export default StepAccordionSection
